/**
 * oracle service — AI conversation routing for the F1 Oracle screen.
 * -------------------------------------------------------------------------
 * The Oracle answers two flavours of question:
 *
 *   1. Telemetry / live race data   →  served LIVE from OpenF1
 *   2. Standings, history, regulations, strategy, trivia  →  mock catalogue
 *      today, LLM-backed in production
 *
 * Routing decisions:
 *
 *   processAIQuery(input)
 *     ├── telemetry intent?  → openF1Client → build dynamic response blocks
 *     │                        (fall back to mock catalogue on error)
 *     └── otherwise           → keyword router over the mock catalogue
 *
 * Why split the paths client-side: telemetry queries have a deterministic
 * answer that the API can serve directly. Wasting LLM cost (or even round
 * trips) on data we already have is unnecessary. When the production LLM
 * backend lands, the same intent split will live there — this client
 * splits it today so the rest of the UI doesn't have to wait.
 */

import { oracleKnowledgeBase, oracleKnowledgeSources } from '../data/oracleKnowledgeBase.js';
import { oracleResponses, defaultOracleResponse } from '../data/oracleResponses.js';
import {
  FACT_INTENTS,
  detectFactIntent,
  describeRace,
  fetchRaceFacts,
  fetchStandingsFacts,
  fetchQualifyingFacts,
  fetchScheduleFacts,
  fetchHeadToHeadFacts,
  fetchSeasonSummaryFacts,
} from './oracleFacts.js';
import {
  LOCAL_AI_MODELS,
  LOCAL_AI_PREFERENCES,
  LOCAL_AI_TIERS,
  getCachedLocalAiCapability,
  getLocalAiPreference,
  getLocalAiRuntime,
} from './localAiCapabilityService.js';
import { fetchFastestLapBreakdown } from './openF1Client.js';
import { askOracleBackend, OracleBackendUnavailable } from './oracleBackendClient.js';

const TELEMETRY_KEYWORDS = [
  'telemetry',
  'sector',
  'fast lap',
  'fastest lap',
  'lap time',
  'mini sector',
  'speed trap',
  'top speed',
];

const LIVE_DATA_KEYWORDS = [
  'current standings',
  'live standings',
  'weather',
  'active penalty',
  'penalty right now',
  'who is leading',
  'currently leading',
  'latest',
  'most recent',
  'this weekend',
  'right now',
  // Result lookups are DATA questions, not knowledge questions. Without these
  // "Who won the last race?" matched a weekend-format explainer and answered
  // from the KB instead of admitting it needs the results API — the app has
  // that data one tab away.
  'who won',
  'who win',
  'winner of',
  'last race',
  'previous race',
  'how did',
  'what position did',
  'where did',
  'pole sitter',
  'race result',
  // Schedule intent. "What time does the race start?" is lexically almost
  // identical to the KB's "how does an f1 race start" but wants a timetable,
  // not a rules explainer.
  'what time',
  'when is',
  'when does',
  'start time',
  'how can i watch',
  'on tv',
];

const SOFT_LIVE_DATA_KEYWORDS = ['current', 'live', 'today', 'now'];

const COMPLEX_OFF_DEVICE_KEYWORDS = [
  'who braked later',
  'braking point',
  'apex speed',
  'corner telemetry',
  'compare telemetry',
  'lap by lap',
  'race simulation',
  'predict',
  'forecast',
  'strategy call for',
];

const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'can',
  'does',
  'for',
  'from',
  'how',
  'is',
  'it',
  'me',
  'of',
  'on',
  'the',
  'to',
  'what',
  'when',
  'why',
  'with',
]);

/**
 * processAIQuery — public entry point used by the Oracle chat composer.
 *
 * @param {string} userInput          raw text from the chat input
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<OracleMessage>}  fully-resolved assistant message
 *
 * /* =====================================================================
 *    OPEN-ENDED PATH — hosted Claude backend (SHIPPED, dev).
 *
 *    Status (2026-07):
 *      ✓ Factual path:    LIVE via Jolpica/OpenF1 (this file) — never the LLM
 *      ✓ Telemetry path:  LIVE via OpenF1 (this file)
 *      ✓ Open-ended path: LIVE via /api/oracle → Anthropic Messages API,
 *                         model claude-opus-4-8 (default; ORACLE_MODEL env),
 *                         streamed back as SSE. See buildHostedLlmResponse
 *                         below + scripts/vite-oracle-proxy.mjs.
 *
 *    Order of routing (first match wins):
 *      FACTS → telemetry → knowledge base → on-device AI → HOSTED LLM → catalogue
 *    The fact bridge runs FIRST and answers results/standings/etc. from real
 *    data, so those never reach the LLM. Only genuinely open-ended questions do.
 *
 *    Security / ops (all handled server-side in the proxy):
 *      - The ANTHROPIC_API_KEY stays on the backend — never bundled. When it is
 *        absent the endpoint 503s and this router falls back to the catalogue.
 *      - The system prompt is server-owned; the client only sends the turns.
 *      - PROD: the dev middleware must be ported to the deployed edge function
 *        (per-user rate limiting + auth belong there). See README → Backend.
 *    ===================================================================== *\/
 */
export async function processAIQuery(userInput, { signal, onStream, history } = {}) {
  if (typeof userInput !== 'string' || !userInput.trim()) {
    throw new Error('processAIQuery: input must be a non-empty string');
  }

  const text = userInput.toLowerCase();

  // FACTUAL PATH — race results and standings, answered from the same services
  // the Results screen uses. Runs FIRST and never falls through to the mock
  // catalogue: that catalogue contains an invented race report, and serving it
  // for "who won the last race?" stated a fiction as fact. See oracleFacts.js.
  const factIntent = detectFactIntent(userInput);
  if (factIntent) {
    // A null result means the intent was detected but the entity couldn't be
    // resolved (e.g. a "vs" question naming no two drivers). Those fall through
    // to the rest of the router; result/standings/schedule/qualifying always
    // answer (data or an honest "couldn't get it"), never null.
    const factResponse = await buildFactResponse(factIntent, userInput, { signal });
    if (factResponse) return factResponse;
  }

  const isTelemetryQuery = TELEMETRY_KEYWORDS.some((kw) => text.includes(kw));

  // LIVE PATH — telemetry intent.
  if (isTelemetryQuery) {
    try {
      const live = await buildLiveTelemetryResponse({ signal });
      if (live) return live;
      // If OpenF1 returned no usable data, fall through to mock telemetry.
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      // Network error / OpenF1 down — fall through to mock content. We log
      // so a developer can spot recurring failures, but the user still gets
      // a useful answer.
      if (typeof console !== 'undefined') {
        console.warn('OpenF1 telemetry fetch failed, falling back to mock', err);
      }
    }
  }

  const knowledgeMatch = findKnowledgeBaseMatch(userInput);
  if (knowledgeMatch && !requiresLiveData(userInput, knowledgeMatch)) {
    try {
      const localResponse = await buildLocalKnowledgeResponse(userInput, knowledgeMatch, { signal });
      if (localResponse) return localResponse;
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      if (typeof console !== 'undefined') {
        console.warn('Local Oracle generation failed, falling back to KB response', err);
      }
    }

    return buildKnowledgeBaseResponse(knowledgeMatch);
  }

  if (canUseGeneralLocalAnswer(userInput)) {
    try {
      const localResponse = await buildGeneralLocalResponse(userInput, { signal });
      if (localResponse) return localResponse;
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      if (typeof console !== 'undefined') {
        console.warn('General local Oracle generation failed, falling back to catalogue', err);
      }
    }
  }

  // HOSTED LLM PATH — open-ended questions (history, rules, strategy, opinions)
  // the fact bridge and knowledge base don't cover. Real conversational AI via
  // the server-side /api/oracle backend (key stays server-side). Streams deltas
  // through onStream. Returns null — falling through to the catalogue — when the
  // backend isn't configured or is unreachable.
  try {
    const hosted = await buildHostedLlmResponse(userInput, { signal, onStream, history });
    if (hosted) return hosted;
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    if (typeof console !== 'undefined') {
      console.warn('Hosted Oracle backend failed, falling back to catalogue', err);
    }
  }

  // MOCK PATH — variable latency keeps the typing indicator feeling real.
  const latency = 900 + Math.random() * 900;
  await simulateLatency(latency, signal);

  const match = routeToResponse(userInput) ?? defaultOracleResponse;
  return finaliseMessage(match.tone, match.blocks);
}

/**
 * Streaming variant — placeholder. Wired to the same mock catalogue today,
 * but its existence locks in the contract the backend team should implement.
 */
export async function* processAIQueryStream(userInput) {
  const final = await processAIQuery(userInput);
  yield { type: 'final', message: final };
}

/* ===========================================================================
 * FACTUAL ANSWERS — built from the results/standings services
 *
 * Every exit from here is either real data or an explicit "couldn't get it".
 * Nothing in this section may fall back to the mock catalogue.
 * ========================================================================= */

async function buildFactResponse(intent, userInput, { signal }) {
  try {
    switch (intent) {
      case FACT_INTENTS.RACE_RESULT:
        return await buildRaceResultResponse(userInput, { signal });
      case FACT_INTENTS.STANDINGS:
        return await buildStandingsResponse(userInput, { signal });
      case FACT_INTENTS.SCHEDULE:
        return await buildScheduleResponse(userInput, { signal });
      case FACT_INTENTS.QUALIFYING:
        return await buildQualifyingResponse(userInput, { signal });
      case FACT_INTENTS.HEAD_TO_HEAD:
        return await buildHeadToHeadResponse(userInput, { signal });
      case FACT_INTENTS.SEASON_SUMMARY:
        return await buildSeasonSummaryResponse(userInput, { signal });
      default:
        return factUnavailable('I could not work out which fact you were after.');
    }
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    if (typeof console !== 'undefined') {
      console.warn('[Oracle] factual lookup failed', err);
    }
    return factUnavailable(
      'I could not reach the results data just now, so I would only be guessing.'
    );
  }
}

/** The honest dead end. Never dressed up as an answer. */
function factUnavailable(reason) {
  return finaliseMessage('conversational', [
    {
      type: 'callout',
      text: `${reason} Try the Results tab — it reads the same source directly, and it will say if the data is missing there too.`,
    },
  ]);
}

async function buildRaceResultResponse(userInput, { signal }) {
  const facts = await fetchRaceFacts(userInput, { signal });

  if (!facts) {
    return factUnavailable("I couldn't find a season with any completed races to report on.");
  }
  if (!facts.track) {
    return factUnavailable(`I have no race calendar for ${facts.season}.`);
  }
  if (facts.upcoming) {
    const when = facts.track.date_start
      ? ` It is scheduled for ${new Date(facts.track.date_start).toUTCString().slice(0, 16)}.`
      : '';
    return factUnavailable(
      `The ${facts.season} ${facts.track.circuit_short_name} Grand Prix hasn't been run yet, so there is no result.${when}`
    );
  }

  const race = facts.race;
  if (race?._status === 'cancelled') {
    return factUnavailable(`The ${facts.season} ${facts.track.circuit_short_name} Grand Prix was cancelled.`);
  }
  if (!race?.results?.length) {
    return factUnavailable(
      `No classification came back for the ${facts.season} ${facts.track.circuit_short_name} Grand Prix.`
    );
  }

  const top = race.results.slice(0, 5);
  const blocks = [
    { type: 'paragraph', text: describeRace(facts) },
    { type: 'heading', text: 'Top 5 finishers' },
    {
      type: 'data',
      label: 'Position · Driver · Gap',
      rows: top.map((row, i) => ({
        k: `${i + 1}.  ${row.driver_name}`,
        v: `${row.team_name} · ${i === 0 ? 'winner' : row.time_gap || row.status}`,
      })),
    },
  ];

  const extras = [];
  if (race.pole_position?.driver_name && race.pole_position.driver_name !== '-') {
    const lap = race.pole_position.lap_time && race.pole_position.lap_time !== '-'
      ? ` (${race.pole_position.lap_time})`
      : '';
    extras.push(`Pole: ${race.pole_position.driver_name}${lap}.`);
  }
  if (race.fastest_lap?.driver_name && race.fastest_lap.driver_name !== '-') {
    const lap = race.fastest_lap.lap_time && race.fastest_lap.lap_time !== '-'
      ? ` — ${race.fastest_lap.lap_time}`
      : '';
    extras.push(`Fastest lap: ${race.fastest_lap.driver_name}${lap}.`);
  }
  if (extras.length > 0) {
    blocks.push({ type: 'callout', text: extras.join(' ') });
  }

  // Provisional means the classification came from live timing rather than the
  // official results, so penalties may not be applied. Say so.
  if (race._provisional) {
    blocks.push({
      type: 'callout',
      text: 'Provisional — this is the on-track order at the flag; post-race penalties may not be reflected.',
    });
  }

  blocks.push({
    type: 'data',
    label: 'Source',
    rows: [
      { k: 'Race', v: `Round ${facts.track.round ?? '?'} · ${facts.season}` },
      { k: 'Data', v: sourceName(race._source) },
    ],
  });

  return finaliseMessage('analytical', blocks);
}

function sourceName(source) {
  if (source === 'livetiming') return 'F1 live timing';
  if (source === 'openf1-backup') return 'OpenF1';
  return 'Jolpica-F1';
}

async function buildStandingsResponse(userInput, { signal }) {
  const facts = await fetchStandingsFacts(userInput, { signal });
  const title = facts.wantsConstructors ? 'Constructors' : 'Drivers';

  if (facts.rows.length === 0) {
    return factUnavailable(`I have no ${title.toLowerCase()} standings for ${facts.season}.`);
  }

  const leader = facts.rows[0];
  const second = facts.rows[1];
  const margin = second ? Math.round((leader.points - second.points) * 100) / 100 : null;

  const lead = second
    ? `${leader.name} leads the ${facts.season} ${title.toLowerCase()} championship on ${leader.points} points, ` +
      `${margin} clear of ${second.name}.`
    : `${leader.name} tops the ${facts.season} ${title.toLowerCase()} championship on ${leader.points} points.`;

  return finaliseMessage('analytical', [
    { type: 'paragraph', text: lead },
    { type: 'heading', text: `${title} championship — top 5` },
    {
      type: 'data',
      label: 'Position · Name · Points',
      rows: facts.rows.slice(0, 5).map((row, i) => ({
        k: `${row.position ?? i + 1}.  ${row.name}`,
        v: `${row.points} pts${row.wins ? ` · ${row.wins} win${row.wins > 1 ? 's' : ''}` : ''}`,
      })),
    },
    {
      type: 'data',
      label: 'Source',
      rows: [
        { k: 'After', v: facts.round ? `Round ${facts.round}` : `${facts.season} season` },
        { k: 'Data', v: 'Jolpica-F1' },
      ],
    },
  ]);
}

async function buildQualifyingResponse(userInput, { signal }) {
  const facts = await fetchQualifyingFacts(userInput, { signal });

  if (!facts.track) {
    return factUnavailable("I couldn't find a race with a qualifying session to report on.");
  }
  if (facts.upcoming) {
    return factUnavailable(
      `Qualifying for the ${facts.season} ${facts.track.circuit_short_name} Grand Prix hasn't run yet.`
    );
  }
  if (facts.rows.length === 0) {
    return factUnavailable(
      `No qualifying classification came back for the ${facts.season} ${facts.track.circuit_short_name} Grand Prix.`
    );
  }

  const pole = facts.rows[0];
  const poleTime = pole.time && pole.time !== '-' ? `, ${pole.time}` : '';

  return finaliseMessage('analytical', [
    {
      type: 'paragraph',
      text: `${pole.name} took pole for the ${facts.season} ${facts.track.circuit_short_name} Grand Prix for ${pole.team}${poleTime}.`,
    },
    { type: 'heading', text: 'Top 5 on the grid' },
    {
      type: 'data',
      label: 'Pos · Driver · Best lap',
      rows: facts.rows.slice(0, 5).map((r) => ({
        k: `${r.position ?? '-'}.  ${r.name}`,
        v: `${r.team} · ${r.time}${r.segment && r.time !== '-' ? ` (${r.segment})` : ''}`,
      })),
    },
    {
      type: 'data',
      label: 'Source',
      rows: [
        { k: 'Session', v: `Qualifying · Round ${facts.track.round ?? '?'} · ${facts.season}` },
        { k: 'Data', v: 'Jolpica-F1' },
      ],
    },
  ]);
}

/* ===========================================================================
 * SCHEDULE — the next race and its weekend timetable
 * ========================================================================= */

/** Support sessions in the order they run, with the labels the UI shows. */
const SCHEDULE_SESSIONS = [
  ['FirstPractice', 'Practice 1'],
  ['SecondPractice', 'Practice 2'],
  ['ThirdPractice', 'Practice 3'],
  ['SprintQualifying', 'Sprint Qualifying'],
  ['SprintShootout', 'Sprint Shootout'],
  ['Sprint', 'Sprint'],
  ['Qualifying', 'Qualifying'],
];

async function buildScheduleResponse(userInput, { signal }) {
  const facts = await fetchScheduleFacts(userInput, { signal });

  if (!facts.race) {
    return factUnavailable(`I don't have a published calendar for ${facts.season} yet.`);
  }

  const race = facts.race;
  const circuit = race.Circuit?.circuitName ?? 'the circuit';
  const place = [race.Circuit?.Location?.locality, race.Circuit?.Location?.country]
    .filter(Boolean)
    .join(', ');
  const where = place ? `${circuit}, ${place}` : circuit;

  const lead = facts.named
    ? `The ${facts.season} ${race.raceName} is at ${where}.${scheduleWhen(race.date, race.time)}`
    : `The next race is the ${facts.season} ${race.raceName} at ${where}.${scheduleWhen(race.date, race.time)}`;

  const rows = [];
  for (const [key, label] of SCHEDULE_SESSIONS) {
    const s = race[key];
    if (s?.date) rows.push({ k: label, v: formatSessionUTC(s.date, s.time) });
  }
  rows.push({ k: 'Race', v: formatSessionUTC(race.date, race.time) });

  return finaliseMessage('analytical', [
    { type: 'paragraph', text: lead },
    { type: 'heading', text: 'Weekend schedule (UTC)' },
    { type: 'data', label: `Round ${race.round} · ${facts.season}`, rows },
    {
      type: 'callout',
      text: 'All times UTC — convert to your local zone. Broadcast varies by region (F1 TV, plus local rights holders).',
    },
  ]);
}

/** "Fri 23 May, 13:30" in UTC, or "Fri 23 May (time TBC)" when Jolpica omits the time. */
function formatSessionUTC(date, time) {
  const d = new Date(time ? `${date}T${time}` : `${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  const day = d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
  if (!time) return `${day} (time TBC)`;
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  return `${day}, ${hm}`;
}

/** A human "how far away" clause. Empty for a race already in the past. */
function scheduleWhen(date, time) {
  const d = new Date(time ? `${date}T${time}` : `${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return ''; // a named race already in the past — no countdown
  if (days === 0) return ' Lights out is today.';
  if (days === 1) return ' It is tomorrow.';
  if (days <= 7) return ` It is ${days} days away.`;
  const on = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', timeZone: 'UTC' });
  return ` It is ${days} days away (${on}).`;
}

/* ===========================================================================
 * HEAD-TO-HEAD + SEASON SUMMARY — resolved entrants, read from their season
 *
 * Both return null (via their fetchers) when no entrant resolves, so the
 * Oracle keeps routing instead of dead-ending a mis-detected question.
 * ========================================================================= */

/** "P3" for a real finishing position, "–" otherwise. */
function fmtPos(position) {
  return Number.isFinite(position) ? `P${position}` : '–';
}

async function buildHeadToHeadResponse(userInput, { signal }) {
  const facts = await fetchHeadToHeadFacts(userInput, { signal });
  if (!facts) return null; // no pair identified — keep routing

  const { a, b, h2h, season } = facts;
  const lead =
    h2h.shared === 0
      ? `In ${season}, ${a.name} and ${b.name} have no completed race in common yet.`
      : h2h.aAhead === h2h.bAhead
        ? `In ${season}, ${a.name} and ${b.name} are level ${h2h.aAhead}–${h2h.bAhead} on race-day finishes across ${h2h.shared} shared races.`
        : `In ${season}, ${h2h.aAhead > h2h.bAhead ? a.name : b.name} leads the race-day head-to-head ` +
          `${Math.max(h2h.aAhead, h2h.bAhead)}–${Math.min(h2h.aAhead, h2h.bAhead)} across ${h2h.shared} shared races.`;

  return finaliseMessage('analytical', [
    { type: 'paragraph', text: lead },
    {
      type: 'data',
      label: `${a.name}  vs  ${b.name}`,
      rows: [
        { k: 'Race finishes (H2H)', v: `${h2h.aAhead} – ${h2h.bAhead}` },
        { k: 'Points', v: `${a.points} – ${b.points}` },
        { k: 'Wins', v: `${a.wins} – ${b.wins}` },
        { k: 'Podiums', v: `${a.podiums} – ${b.podiums}` },
        { k: 'Best finish', v: `${fmtPos(a.bestFinish)} – ${fmtPos(b.bestFinish)}` },
        { k: 'DNFs', v: `${a.dnfs} – ${b.dnfs}` },
      ],
    },
    { type: 'data', label: 'Source', rows: [{ k: 'Season', v: `${season}` }, { k: 'Data', v: 'Jolpica-F1' }] },
  ]);
}

async function buildSeasonSummaryResponse(userInput, { signal }) {
  const facts = await fetchSeasonSummaryFacts(userInput, { signal });
  if (!facts) return null; // no driver or team identified — keep routing

  const s = facts.summary;
  const unit = facts.kind === 'constructor' ? 'entries' : 'races';
  const standing = facts.standing ? ` — currently P${facts.standing} in the championship` : '';
  const line =
    `${facts.name}'s ${facts.season} season: ${s.wins} win${s.wins === 1 ? '' : 's'}, ` +
    `${s.podiums} podium${s.podiums === 1 ? '' : 's'} and ${s.points} points from ${s.entries} ${unit}${standing}.`;

  const rows = [];
  if (facts.standing) rows.push({ k: 'Championship', v: `P${facts.standing}` });
  rows.push(
    { k: 'Wins', v: `${s.wins}` },
    { k: 'Podiums', v: `${s.podiums}` },
    { k: 'Best finish', v: fmtPos(s.bestFinish) },
    { k: 'DNFs', v: `${s.dnfs}` },
    { k: 'Points', v: `${s.points}` }
  );

  const label =
    facts.kind === 'constructor'
      ? `${facts.name}${facts.drivers?.length ? ` · ${facts.drivers.join(' & ')}` : ''}`
      : `${facts.name}${facts.team ? ` · ${facts.team}` : ''}`;

  return finaliseMessage('analytical', [
    { type: 'paragraph', text: line },
    { type: 'data', label, rows },
    { type: 'data', label: 'Source', rows: [{ k: 'After', v: `${facts.season} season` }, { k: 'Data', v: 'Jolpica-F1' }] },
  ]);
}

/* ===========================================================================
 * LIVE TELEMETRY — built from OpenF1
 * ========================================================================= */

async function buildLiveTelemetryResponse({ signal }) {
  const data = await fetchFastestLapBreakdown({ signal });
  if (!data) return null;

  const { session, driver, fastest, topSpeedKmh } = data;

  const driverLabel = driver
    ? `${driver.full_name} (${driver.name_acronym})`
    : `Driver #${fastest.driver_number}`;
  const teamLabel = driver?.team_name ?? 'Unknown team';
  const meetingLabel = formatMeeting(session);

  const lapTime = formatLapTime(fastest.lap_duration);
  const s1 = formatSeconds(fastest.duration_sector_1);
  const s2 = formatSeconds(fastest.duration_sector_2);
  const s3 = formatSeconds(fastest.duration_sector_3);

  const dataRows = [
    { k: 'Sector 1', v: `${s1}s` },
    { k: 'Sector 2', v: `${s2}s` },
    { k: 'Sector 3', v: `${s3}s` },
  ];
  if (typeof fastest.i1_speed === 'number') {
    dataRows.push({ k: 'Speed trap I1', v: `${fastest.i1_speed} km/h` });
  }
  if (typeof fastest.i2_speed === 'number') {
    dataRows.push({ k: 'Speed trap I2', v: `${fastest.i2_speed} km/h` });
  }
  if (typeof fastest.st_speed === 'number') {
    dataRows.push({ k: 'Speed trap S/F', v: `${fastest.st_speed} km/h` });
  }
  if (topSpeedKmh) {
    dataRows.push({ k: 'Top speed (peak)', v: `${topSpeedKmh} km/h` });
  }

  return finaliseMessage('analytical', [
    {
      type: 'paragraph',
      text: `Live from OpenF1 — the fastest lap of the ${meetingLabel} was a ${lapTime} by ${driverLabel} on lap ${fastest.lap_number}. ${teamLabel} on the timing screens.`,
    },
    {
      type: 'heading',
      text: 'Sector breakdown',
    },
    {
      type: 'data',
      label: 'Live · sourced from api.openf1.org',
      rows: dataRows,
    },
    {
      type: 'callout',
      text: `Data refreshed at ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC' })} UTC. Open the Results screen to see this meeting's full classification.`,
    },
  ]);
}

/* ===========================================================================
 * Helpers
 * ========================================================================= */

function finaliseMessage(tone, blocks) {
  return {
    id: `oracle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role: 'assistant',
    tone,
    blocks,
    timestamp: new Date().toISOString(),
  };
}

function routeToResponse(userInput) {
  const text = userInput.toLowerCase();
  for (const response of oracleResponses) {
    if (response.keywords.some((kw) => text.includes(kw))) {
      return response;
    }
  }
  return null;
}

async function buildLocalKnowledgeResponse(userInput, entry, { signal } = {}) {
  const adapter = getLocalAiRuntime();
  if (!adapter) return null;

  const generate = adapter.generateAnswer || adapter.generateText;
  if (typeof generate !== 'function') return null;

  const modelId = selectLocalOracleModelId();
  if (!modelId) return null;

  const prompt = buildLocalKnowledgePrompt(userInput, entry);
  const result = await generate({
    modelId,
    prompt,
    maxOutputTokens: 180,
    signal,
  });

  const text = sanitiseLocalAnswer(result?.text || result?.answer || '');
  if (!text) return null;

  return finaliseMessage('conversational', [
    {
      type: 'paragraph',
      text,
    },
    {
      type: 'callout',
      text: `Local AI · grounded in ${entry.title}${sourceLabel(entry)}`,
    },
  ]);
}

async function buildGeneralLocalResponse(userInput, { signal } = {}) {
  const adapter = getLocalAiRuntime();
  if (!adapter) return null;

  const generate = adapter.generateAnswer || adapter.generateText;
  if (typeof generate !== 'function') return null;

  const modelId = selectLocalOracleModelId();
  if (!modelId) return null;

  const prompt = buildGeneralLocalPrompt(userInput);
  const result = await generate({
    modelId,
    prompt,
    maxOutputTokens: 260,
    signal,
  });

  const text = sanitiseLocalAnswer(result?.text || result?.answer || '');
  if (!text) return null;

  return finaliseMessage('conversational', [
    {
      type: 'paragraph',
      text,
    },
    {
      type: 'callout',
      text: 'Local AI · general F1 answer. Live facts, current standings, race-control decisions, and telemetry still need data tools.',
    },
  ]);
}

/* ===========================================================================
 * HOSTED LLM — open-ended answers via the /api/oracle backend
 * ========================================================================= */

/** How many prior turns of context to send the model. */
const HISTORY_TURNS = 8;

const MODEL_DISPLAY_NAMES = {
  'claude-opus-4-8': 'Claude Opus 4.8',
  'claude-opus-4-7': 'Claude Opus 4.7',
  'claude-sonnet-5': 'Claude Sonnet 5',
  'claude-haiku-4-5': 'Claude Haiku 4.5',
  'claude-fable-5': 'Claude Fable 5',
};

/** A friendly label for the model that answered, for the attribution footer. */
export function modelDisplayName(model) {
  if (!model) return 'Claude';
  if (MODEL_DISPLAY_NAMES[model]) return MODEL_DISPLAY_NAMES[model];
  const base = String(model).replace(/-\d{8}$/, ''); // drop a dated snapshot suffix
  return MODEL_DISPLAY_NAMES[base] ?? 'Claude';
}

/** Plain-text prose → paragraph blocks (single newlines collapsed to spaces). */
export function textToBlocks(text) {
  return String(text ?? '')
    .split(/\n{2,}/)
    .map((para) => para.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean)
    .map((para) => ({ type: 'paragraph', text: para }));
}

/** Flatten a rendered assistant message's blocks back to plain text for the
 *  model's context. Callouts are dropped — they're UI hints/attribution, not
 *  answer content. */
function blocksToPlainText(blocks) {
  const parts = [];
  for (const block of blocks ?? []) {
    switch (block.type) {
      case 'paragraph':
      case 'heading':
      case 'quote':
        if (block.text) parts.push(block.text);
        break;
      case 'list':
        if (Array.isArray(block.items)) parts.push(block.items.join('; '));
        break;
      case 'data':
        if (Array.isArray(block.rows)) {
          parts.push(block.rows.map((row) => `${row.k}: ${row.v}`).join('; '));
        }
        break;
      default:
        break; // callout and unknowns: skip
    }
  }
  return parts.join('\n').trim();
}

/** Map the UI's message list to the Anthropic {role, content} shape, keeping the
 *  last HISTORY_TURNS and ensuring the array starts on a user turn. */
export function historyToApiMessages(history) {
  if (!Array.isArray(history)) return [];
  const out = [];
  for (const msg of history.slice(-HISTORY_TURNS)) {
    if (msg?.role === 'user' && typeof msg.text === 'string' && msg.text.trim()) {
      out.push({ role: 'user', content: msg.text.trim() });
    } else if (msg?.role === 'assistant' && Array.isArray(msg.blocks)) {
      const content = blocksToPlainText(msg.blocks);
      if (content) out.push({ role: 'assistant', content });
    }
  }
  while (out.length && out[0].role !== 'user') out.shift();
  return out;
}

/**
 * buildHostedLlmResponse — ask the hosted Claude backend an open-ended question.
 * Returns null (→ fall back to the catalogue) when the backend is unavailable or
 * returns nothing. Streams partial text through onStream for the live bubble.
 */
async function buildHostedLlmResponse(userInput, { signal, onStream, history } = {}) {
  const apiMessages = [...historyToApiMessages(history), { role: 'user', content: userInput }];

  let result;
  try {
    result = await askOracleBackend(apiMessages, { signal, onDelta: onStream });
  } catch (err) {
    if (err instanceof OracleBackendUnavailable) return null; // → catalogue fallback
    throw err;
  }

  if (!result.text) return null;

  const blocks = textToBlocks(result.text);
  blocks.push({
    type: 'callout',
    text: `${modelDisplayName(result.model)} · general F1 knowledge, not live data. For current standings or results, check the Results tab.`,
  });
  return finaliseMessage('conversational', blocks);
}

function buildKnowledgeBaseResponse(entry) {
  const blocks = [
    {
      type: 'paragraph',
      text: entry.answer.summary,
    },
  ];

  if (entry.answer.keyPoints?.length) {
    blocks.push({ type: 'list', items: entry.answer.keyPoints });
  }

  if (entry.answer.caveats?.length) {
    blocks.push({
      type: 'callout',
      text: entry.answer.caveats.join(' '),
    });
  }

  blocks.push({
    type: 'callout',
    text: `Offline KB · ${entry.title}${sourceLabel(entry)}`,
  });

  return finaliseMessage('conversational', blocks);
}

function buildLocalKnowledgePrompt(userInput, entry) {
  const facts = knowledgeFacts(entry).map((fact) => `- ${fact}`).join('\n');

  return `You are Oracle, an F1 assistant inside F1 Central.
Use the facts below as ground truth, but answer naturally and explain the idea like a knowledgeable F1 analyst.
You may add general F1 context if it does not contradict the facts.
Do not invent live results, current standings, telemetry, penalties, weather, or precise race numbers.
If the user needs current/live data, say that Oracle needs live data tools for that part.

Facts:
${facts}

User question: ${userInput}
Answer:`;
}

function buildGeneralLocalPrompt(userInput) {
  return `You are Oracle, an F1 assistant inside F1 Central, answering OFFLINE from a small on-device model.
Explain F1 concepts, rules, strategy, and how things work — clearly and conversationally.

Hard limits, because you are offline and cannot verify anything:
- Do NOT state specific facts you would have to look up: race results, winners, records, championship outcomes, points totals, lap times, dates, or who did what in a particular event. If the question turns on such a specific, say you would need a connection to confirm it, then answer only the general part you are sure of.
- Do NOT invent names, numbers, results, or dates to fill a gap. An honest "I can't confirm that offline" is the correct answer, not a guess.
- Prefer explaining the idea over recounting history.
Keep it concise and useful. Avoid saying "I don't have a curated answer".

User question: ${userInput}
Answer:`;
}

function knowledgeFacts(entry) {
  return [
    entry.answer.summary,
    ...(entry.answer.keyPoints || []),
    ...(entry.answer.caveats || []).map((caveat) => `Caveat: ${caveat}`),
  ].filter(Boolean);
}

function selectLocalOracleModelId() {
  const capability = getCachedLocalAiCapability();
  const preference = getLocalAiPreference();
  const standardModelDeleted = capability?.standardModelDeleted === true;

  if (preference === LOCAL_AI_PREFERENCES.onlineOnly) return null;

  const standardAvailable = [
    LOCAL_AI_TIERS.standard,
    LOCAL_AI_TIERS.enhancedCandidate,
    LOCAL_AI_TIERS.enhanced,
  ].includes(capability?.tier);
  const enhancedAvailable = capability?.tier === LOCAL_AI_TIERS.enhanced;

  if (preference === LOCAL_AI_PREFERENCES.enhanced) {
    return enhancedAvailable ? LOCAL_AI_MODELS.enhanced.modelId : null;
  }

  if (preference === LOCAL_AI_PREFERENCES.standard) {
    return standardAvailable && !standardModelDeleted ? LOCAL_AI_MODELS.standard.modelId : null;
  }

  if (preference === LOCAL_AI_PREFERENCES.auto) {
    // Local AI is the OFFLINE tier by default. When the device is online the
    // hosted tiers are more accurate and cost a rounding error, so 'auto' skips
    // local unless there's no connection. A user who explicitly prefers on-device
    // (the standard/enhanced preferences above — the privacy opt-in) still gets
    // local while online. Reserving local for offline also keeps the weakest
    // model out of ungrounded fact-answering when a better path is available.
    if (!isOffline()) return null;
    if (standardModelDeleted && enhancedAvailable) return LOCAL_AI_MODELS.enhanced.modelId;
    return standardAvailable ? LOCAL_AI_MODELS.standard.modelId : null;
  }

  return null;
}

/**
 * isOffline — true only when the browser is definitively offline. Undefined
 * navigator (SSR/tests/Node) is treated as online so nothing is gated there.
 */
function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * KB_MATCH_THRESHOLD — a match needs either a query-phrase hit (120) or two
 * genuinely independent weaker signals. Deliberately above the score of any
 * single tag so one incidental keyword can no longer carry a match on its own.
 */
const KB_MATCH_THRESHOLD = 95;

/**
 * containsPhrase — word-boundary containment, NOT raw substring.
 *
 * The previous implementation used `input.includes(tag)`, so the three-letter
 * tag "ers" matched inside "v-ERS-tappen" and scored 65 against a threshold of
 * 55. That single accident is why "How tall is Max Verstappen?" returned a
 * confident explainer on hybrid power units. A sweep of 18 deliberately
 * off-topic questions produced 6 confident KB answers (33%).
 */
function containsPhrase(haystack, needle) {
  if (!needle) return false;
  return ` ${haystack} `.includes(` ${needle} `);
}

/**
 * tagWeight — how much evidence one tag actually provides.
 *
 * 580 tags exist and 87 of them are five characters or shorter ("f1", "gap",
 * "wet", "sc", "abs"). Those are far too generic to carry a match alone, while
 * a multi-word tag ("undercut and overcut") is strong evidence.
 */
function tagWeight(tag) {
  if (tag.includes(' ')) return 60;
  if (tag.length >= 6) return 35;
  if (tag.length >= 3) return 18;
  return 0; // "f1", "q1", "sc" — pure noise
}

function findKnowledgeBaseMatch(userInput) {
  const normalisedInput = normaliseText(userInput);
  const inputTokens = tokenise(normalisedInput);
  let best = null;

  for (const entry of oracleKnowledgeBase) {
    let score = 0;

    // Query matching is TOKEN-COVERAGE, not substring. KB queries are written
    // without articles ("what is halo"), so a user typing "what is the halo"
    // never contained the query as a literal substring — the old code only
    // appeared to work because its loose tag matching papered over the gap.
    // Requiring every meaningful query token to be present keeps it precise
    // while tolerating filler words and reordering.
    for (const query of entry.queries || []) {
      const queryTokens = tokenise(query);
      if (queryTokens.size === 0) continue;
      let covered = 0;
      for (const token of queryTokens) {
        if (inputTokens.has(token)) covered++;
      }
      if (covered === queryTokens.size) {
        // A fully-covered multi-token query is the strongest signal we have;
        // a single-token one is good but not quite as decisive.
        score += queryTokens.size >= 2 ? 120 : 100;
      } else if (queryTokens.size >= 3 && covered / queryTokens.size >= 0.75) {
        // Near-miss on a long query — usually one incidental word the KB author
        // included and the user didn't ("what happens UNDER red flag"). Worth
        // partial credit, but not enough to carry a match on its own.
        score += 50;
      }
    }

    for (const tag of entry.tags || []) {
      const normalisedTag = normaliseText(tag);
      if (containsPhrase(normalisedInput, normalisedTag)) {
        score += tagWeight(normalisedTag);
      } else {
        // Partial credit when the input mentions a distinctive word from a
        // multi-word tag — capped so it cannot accumulate into a false match.
        const tagWords = normalisedTag.split(' ').filter((w) => w.length > 4);
        let partial = 0;
        for (const word of tagWords) {
          if (inputTokens.has(word)) partial += 12;
        }
        score += Math.min(partial, 24);
      }
    }

    for (const token of tokenise(`${entry.title} ${entry.category}`)) {
      if (inputTokens.has(token)) score += 14;
    }

    if (!best || score > best.score) best = { entry, score };
  }

  return best?.score >= KB_MATCH_THRESHOLD ? best.entry : null;
}

function requiresLiveData(userInput, entry) {
  const text = normaliseText(userInput);
  if (LIVE_DATA_KEYWORDS.some((keyword) => text.includes(keyword))) return true;

  const routeHints = entry.answer.routeLiveDataWhen || [];
  if (!routeHints.length) return false;

  return SOFT_LIVE_DATA_KEYWORDS.some((keyword) => text.includes(keyword)) ||
    routeHints.some((hint) => text.includes(normaliseText(hint)));
}

function canUseGeneralLocalAnswer(userInput) {
  const text = normaliseText(userInput);
  if (TELEMETRY_KEYWORDS.some((keyword) => text.includes(keyword))) return false;
  if (LIVE_DATA_KEYWORDS.some((keyword) => text.includes(keyword))) return false;
  if (COMPLEX_OFF_DEVICE_KEYWORDS.some((keyword) => text.includes(keyword))) return false;
  return Boolean(selectLocalOracleModelId());
}

function sourceLabel(entry) {
  const labels = (entry.sourceRefs || [])
    .map((ref) => oracleKnowledgeSources[ref]?.publisher)
    .filter(Boolean);
  if (!labels.length) return '';
  return ` · sources: ${[...new Set(labels)].join(', ')}`;
}

function sanitiseLocalAnswer(value) {
  return String(value)
    .replace(/^Answer:\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normaliseText(value) {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenise(value) {
  return new Set(
    normaliseText(value)
      .split(' ')
      .filter((token) => token.length > 2 && !STOP_WORDS.has(token))
  );
}

/**
 * 97.412 → "1:37.412"  ·  guards against null / 0 / negative durations
 */
function formatLapTime(seconds) {
  if (typeof seconds !== 'number' || seconds <= 0) return '—';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds - minutes * 60;
  return `${minutes}:${rest.toFixed(3).padStart(6, '0')}`;
}

function formatSeconds(seconds) {
  if (typeof seconds !== 'number' || seconds <= 0) return '—';
  return seconds.toFixed(3);
}

function formatMeeting(session) {
  const where = session.circuit_short_name || session.location || 'circuit';
  const country = session.country_name ? `, ${session.country_name}` : '';
  const year = session.year ? ` ${session.year}` : '';
  return `${session.session_name} at ${where}${country}${year}`;
}

function simulateLatency(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      });
    }
  });
}
